import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Bell, Sun, Moon, Menu, LogOut as LogOutIcon, Settings, LifeBuoy } from 'lucide-react';
import { useI18n } from '@/providers/I18nProvider';
import { useAuth } from '@/providers/AuthProvider';
import { useTheme } from '@/providers/ThemeProvider';
import { logout } from '@/lib/auth';
import { Avatar } from '@/components/ui/Avatar';
import type { Locale } from '@/lib/i18n';
import { useSupportUnread } from '@/features/support/hooks';

interface Props {
  onOpenSidebar: () => void;
}

export function Topbar({ onOpenSidebar }: Props) {
  const { t, locale, setLocale, available } = useI18n();
  const { user } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const supportUnread = useSupportUnread();

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen]);

  const avatarSrc = user.has_avatar && user.avatar_hash
    ? `${import.meta.env.VITE_API_BASE_URL}/user/avatar/small?user_id=${user.user_id}&hash=${encodeURIComponent(user.avatar_hash)}`
    : undefined;

  return (
    <header className="topbar">
      <button className="topbar__action-btn" onClick={onOpenSidebar} aria-label="Menü" style={{ display: 'none' }}>
        <Menu size={18} aria-hidden />
      </button>

      <div className="topbar__search">
        <Search className="topbar__search-icon" size={16} aria-hidden />
        <input
          type="search"
          className="topbar__search-input"
          placeholder={t('topbar.search.placeholder')}
          aria-label={t('topbar.search.placeholder')}
        />
      </div>

      <div className="topbar__actions">
        <select
          className="select"
          aria-label={t('topbar.language')}
          value={locale}
          onChange={(e) => setLocale(e.target.value as Locale)}
          style={{ width: 'auto', padding: '6px 8px' }}
        >
          {available.map((l) => (
            <option key={l.code} value={l.code}>{l.flag} {l.name}</option>
          ))}
        </select>

        <button
          className="topbar__action-btn"
          onClick={toggleTheme}
          aria-label={theme === 'dark' ? t('topbar.theme.light') : t('topbar.theme.dark')}
        >
          {theme === 'dark' ? <Sun size={18} aria-hidden /> : <Moon size={18} aria-hidden />}
        </button>

        <Link to="/support" className="topbar__action-btn" aria-label={t('nav.support')}>
          <LifeBuoy size={18} aria-hidden />
          {supportUnread > 0 ? <span className="topbar__badge">{supportUnread}</span> : null}
        </Link>

        <button className="topbar__action-btn" aria-label={t('topbar.notifications')}>
          <Bell size={18} aria-hidden />
        </button>

        <div className="user-menu" ref={menuRef}>
          <button
            type="button"
            className="user-menu__trigger"
            onClick={() => setMenuOpen((o) => !o)}
            aria-expanded={menuOpen}
          >
            <Avatar vorname={user.vorname} nachname={user.nachname} src={avatarSrc} size="sm" />
            <span style={{ fontSize: 13 }}>{user.vorname}</span>
          </button>

          {menuOpen ? (
            <div className="user-menu__dropdown" role="menu">
              <div style={{ padding: '8px 10px' }}>
                <div style={{ fontWeight: 600 }}>{user.vorname} {user.nachname}</div>
                <div className="text-muted" style={{ fontSize: 12 }}>{user.email}</div>
                {user.organisation ? (
                  <div className="text-muted" style={{ fontSize: 12 }}>{user.organisation}</div>
                ) : null}
              </div>
              <div className="user-menu__divider" />
              <Link className="user-menu__item" to="/account" onClick={() => setMenuOpen(false)}>
                <Settings size={14} aria-hidden /> {t('topbar.user.account')}
              </Link>
              <button className="user-menu__item" onClick={() => void logout()} type="button">
                <LogOutIcon size={14} aria-hidden /> {t('topbar.user.logout')}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}
